<template>
  <mf-loading-dialog :loading="$apollo.loading">
    <base-page-layout ref="basePage" class="email-marketing" :title="pageTitle" show-tour :flow-id="flowId">
      <v-container fluid class="pa-0">
        <v-row no-gutters>
          <v-col cols="12">
            <title-description
              :title.sync="campaign.title"
              :description.sync="campaign.description"
              :title-error="$v.campaign.title && $v.campaign.title.$error"
              :title-error-message="errorTitle"
              :description-error="$v.campaign.description && $v.campaign.description.$error"
              :description-error-message="errorDescription"
              :status="campaign.status"
            ></title-description>
          </v-col>
        </v-row>

        <v-row class="mt-3">
          <v-col v-if="isOfferSchedulePermissionActive" cols="3" class="d-flex">
            <offer-schedule-card :offer-schedule="offerSchedule" />
          </v-col>

          <v-col :cols="isOfferSchedulePermissionActive ? 9 : 12" class="d-flex flex-column">
            <products-configuration v-if="!$getFirstValue()" :products.sync="campaign.products" class="mb-6 fill-height" />

            <segments-configuration
              :segments.sync="campaign.segments"
              :error="getSafe(() => $v.campaign.segments.$error)"
              :is-offer-schedule-personalized="isOfferSchedulePersonalized"
              class="fill-height"
            />
          </v-col>
        </v-row>

        <email-types-composer
          v-if="templateTab !== null"
          ref="emailTypesComposer"
          v-model="templateTab"
          :campaign="campaign"
          :validations="$v"
          :tryingToSaveAsDraft="tryingToSaveAsDraft"
          :goToLink="goToLink"
          :header-image-url="campaign.message.simple_email_configuration.header_image"
          :delete-header-image="deleteHeaderImage"
          :upload-header-image="modifyHeaderImg"
          :body-image-url="campaign.message.simple_email_configuration.image"
          :delete-body-image="deleteBodyImage"
          :upload-body-image="modifyBodyImg"
        />

        <div class="text-end">
          <mf-button label="Enviar Preview" data-cy="send-preview-modal-btn" @click="openEmailPreview()" />
          <send-email-preview-modal v-model="openDialog" :campaign="campaign" />
        </div>

        <v-row class="mt-3">
          <v-col cols="12">
            <measuring-and-recurrency v-model="campaign" :validations="$v" />
          </v-col>
        </v-row>

        <v-row class="mt-3">
          <v-col v-for="(card, index) in cards" :key="index" cols="3">
            <cbm-card :number-text="card.value" :title="card.text" :is-currency="card.isCurrency" :boolean-loader="segmentsLoading" :tooltip="card.helpText" />
          </v-col>
          <v-col cols="3">
            <cbm-card :number-text="`${localeString(credits)}`" title="Créditos disponíveis" :boolean-loader="$apollo.queries.credits.loading" />
          </v-col>
        </v-row>

        <mf-action-buttons :primary-button="primaryButton" :secondary-button="secondaryButton" :extra-button="deleteButton" :cancel-button="cancelButton" />
      </v-container>
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'
import SendEmailPreviewModal from '@/modules/campanhas/campanhas-email/views/components/SendEmailPreviewModal.vue'
import { required, minLength, maxLength, minValue } from 'vuelidate/lib/validators'
import { notOnlySpace, isFuture, timeIsFuture, isUrl } from '@/helpers/validators.js'
import { characterVariables } from '@/mixins/character-variables'
import userflow from 'userflow.js'
import { cloneDeep } from 'lodash'
import Swal from 'sweetalert2'
import {
  MUTATION_UPSERT_EMAIL_CAMPAIGN,
  MUTATION_DELETE_CAMPAIGN,
  MUTATION_UPSERT_DRAFT_EMAIL_CAMPAIGN,
  QUERY_GET_SEGMENTS_ESTIMATES,
  QUERY_GET_CREDITS,
} from '../../graphql'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
import { mapActions, mapGetters } from 'vuex'
import { deepDelete } from '@/helpers/deepDelete'
import { actionButtons } from '../../../views/mixins/action-buttons.js'
import CbmCard from '@/components/CbmCard.vue'
import { localeString } from '@/config/helpers.js'
export default {
  components: {
    BasePageLayout,
    TitleDescription,
    SendEmailPreviewModal,
    CbmCard,
    EmailTypesComposer: () => import('@/modules/campanhas/campanhas-email/views/components/EmailTypesComposer.vue'),
    OfferScheduleCard: () => import('@/modules/campanhas/views/components/edit/OfferScheduleCard.vue'),
    ProductsConfiguration: () => import('@/modules/campanhas/views/components/edit/ProductsConfiguration.vue'),
    SegmentsConfiguration: () => import('@/modules/campanhas/views/components/edit/SegmentsConfiguration.vue'),
    MeasuringAndRecurrency: () => import('@/modules/campanhas/views/components/edit/MeasuringAndRecurrency.vue'),
  },

  mixins: [characterVariables, actionButtons],

  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    offerSchedule: {
      type: Object,
      default: () => ({}),
    },
    isOfferSchedulePermissionActive: {
      type: Boolean,
      default: false,
    },
    isOfferSchedulePersonalized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      templateTab: null,
      showAllProducts: false,
      showAllSegments: false,
      credits: null,
      segmentsLoading: false,
      selectedTemplate: {},
      segmentsEstimates: {
        sendingCost: 0,
        sendingQuantity: 0,
        validationCost: 0,
        validationQuantity: 0,
        totalCost: 0,
      },
      loading: {
        scheduled: {
          submit: false,
          disable: false,
        },
        draft: {
          submit: false,
          submitAsDraft: false,
        },
      },
      tryingToSaveAsDraft: false,
      redirectPage: 'email',
      tryingSendEmailPreview: false,
      openDialog: false,
    }
  },

  computed: {
    ...mapGetters({ quickAction: 'getQuickAction' }),

    flowId() {
      const insideBuilderTabUserflowId = '674b8cdf-ac0d-44c2-bc14-47524e9a3a1c'
      const outsideBuilderTabUserflowId = 'e8b3aa5a-e69e-4550-a120-701ab59d81fc'

      if (this.templateTab === 1) {
        userflow.identify(this.$getLoggedUser().id_user, {
          name: this.$getLoggedUser().name,
          email: this.$getLoggedUser().username,
          client: this.$getLoggedUser().client,
          signed_up_at: new Date().toISOString(),
          profiles: this.$getLoggedUser().profiles?.toString(),
          isAdmin: this.$getLoggedUser().mf,
          isFranchise: this.$getLoggedUser().is_franchise,
          isFranchisee: this.$getLoggedUser().is_franchisee,
        })
        userflow.track('email_campaign_builder_tab')

        return insideBuilderTabUserflowId
      }
      return outsideBuilderTabUserflowId
    },

    selectedEmailType() {
      return {
        0: 'SIMPLE',
        1: 'BUILDER',
        2: 'HTML',
      }[this.templateTab]
    },

    tabTypes() {
      return {
        SIMPLE: 0,
        BUILDER: 1,
        HTML: 2,
      }
    },

    maxSubjectLength() {
      return this.lengthCounter(this.campaign.message.simple_email_configuration?.subject, 50)
    },
    maxTextLength() {
      return this.lengthCounter(this.campaign.message.simple_email_configuration?.text, 2000)
    },
    maxButtonTextLength() {
      return this.lengthCounter(this.campaign.message.simple_email_configuration?.click_to_call_button_text, 20)
    },
    maxFooterLength() {
      return this.lengthCounter(this.campaign.message.simple_email_configuration?.footer, 250)
    },
    pageTitle() {
      if (this.$route.params.id) return 'Editar campanha'
      return 'Nova campanha'
    },
    segmentsIds() {
      return this.campaign.segments.map((segment) => segment._id)
    },
    errorTitle() {
      if (!this.$v.campaign.title) return ''
      if (this.$v.campaign.title.$error) {
        if (!this.$v.campaign.title.required) return 'Dê um nome para esta campanha.'
        else if (!this.$v.campaign.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.campaign.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorDescription() {
      if (!this.$v.campaign.description) return ''
      if (this.$v.campaign.description.$error) {
        if (!this.$v.campaign.description.required) return 'Informe detalhes para melhor identificação da campanha.'
        else if (!this.$v.campaign.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.campaign.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    cards() {
      return [
        {
          text: 'Custo das validações',
          value: localeString(this.segmentsEstimates.validationCost || 0),
          isCurrency: false,
          isPercent: false,
          helpText: 'Cada validação de e-mail custa 6 créditos.',
        },
        {
          text: 'Custo dos disparos',
          value: localeString(this.segmentsEstimates.sendingCost || 0),
          isCurrency: false,
          isPercent: false,
          helpText: 'Cada e-mail enviado custa 1 crédito.',
        },
        {
          text: 'Custo estimado do envio',
          value: localeString(this.segmentsEstimates.totalCost || 0),
          isCurrency: false,
          isPercent: false,
          helpText: 'Custo para o envio da campanha considerando validações e envios.',
        },
      ]
    },
    formatedLink() {
      if (this.campaign.message.simple_email_configuration.click_to_call_button_link.includes('http')) {
        return this.campaign.message.simple_email_configuration.click_to_call_button_link
      }
      return `http://${this.campaign.message.simple_email_configuration.click_to_call_button_link}`
    },
  },
  apollo: {
    credits: {
      query: QUERY_GET_CREDITS,
      update({ balance }) {
        return balance
      },
    },
  },

  watch: {
    segmentsIds: {
      handler(segmentsUpdated) {
        if (!segmentsUpdated.length) {
          this.estimatedCost = 0
          return
        }
        this.segmentsLoading = true
        this.$apollo
          .query({
            query: QUERY_GET_SEGMENTS_ESTIMATES,
            variables: {
              segments_ids: segmentsUpdated,
            },
          })
          .then(({ data }) => {
            const res = data.getSegmentsEstimates.email
            this.segmentsEstimates.sendingCost = res.cost.sending
            this.segmentsEstimates.sendingQuantity = res.quantity.sending
            this.segmentsEstimates.validationCost = res.cost.validation
            this.segmentsEstimates.validationQuantity = res.quantity.validation
            this.segmentsEstimates.totalCost = res.cost.credits
            this.segmentsLoading = false
          })
      },
    },
    selectedTemplate() {
      this.campaign.template_id = this.selectedTemplate._id
      this.campaign.message.simple_email_configuration.text = this.selectedTemplate.message
    },
    templateTab() {
      if (this.templateTab === 0) {
        this.campaign.message.html_email_configuration = {}
        this.campaign.message.builder_email_configuration = {}
      } else if (this.templateTab === 1) {
        this.campaign.message.simple_email_configuration = {}
        this.campaign.message.html_email_configuration = {}
      } else {
        this.campaign.message.simple_email_configuration = {}
        this.campaign.message.builder_email_configuration = {}
      }
    },
  },

  mounted() {
    this.prebuildAutoCampaign()
    this.templateTab = this.tabTypes[this.campaign.message.email_configuration_type] || 0
  },

  methods: {
    ...mapActions(['unsetQuickAction']),

    cancel() {
      this.$confirm({ title: 'Descartar alterações', message: 'Todas as alterações serão descartadas.', confirmText: 'Ok', cancelText: 'Cancelar' })
        .then(() => {
          this.returnToList()
        })
        .catch(() => {})
    },
    async remove({ loader }) {
      try {
        this.$confirm({
          title: 'Remover rascunho',
          message: 'Tem certeza que deseja remover este rascunho?',
          confirmText: 'Remover',
          confirmColor: 'vermelho-d20',
          cancelText: 'Cancelar',
        })
          .then(async () => {
            this.$wait.start(loader)

            await this.$apollo.mutate({
              mutation: MUTATION_DELETE_CAMPAIGN,
              variables: {
                id: this.campaign._id,
              },
              context: {
                uri: this.$microservicesUrls['campaigns'],
              },
            })

            this.$wait.end(loader)

            this.$snackbar({ message: 'Rascunho apagado.', snackbarColor: 'success' })
            this.returnToList()
          })
          .catch(() => {})
      } catch (error) {}
    },
    async loadCampaignEmailConfiguration() {
      const messageConfiguration = await this.$refs.emailTypesComposer.getConfiguration()
      this.campaign.message = { ...this.campaign.message, ...messageConfiguration }
    },
    async openEmailPreview() {
      this.tryingSendEmailPreview = true
      await this.loadCampaignEmailConfiguration()
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha os campos de formatação do e-mail para enviar a preview. ', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
        return
      }
      this.openDialog = true
    },
    async save(isDraft) {
      try {
        this.tryingToSaveAsDraft = isDraft
        await this.loadCampaignEmailConfiguration()
        this.$v.$reset()
        this.$v.$touch()
        if (this.$v.$error) {
          this.$snackbar({ message: 'Preencha todos os campos corretamente.', snackbarColor: 'error' })
          this.$nextTick(() => {
            this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
          })

          return
        }

        const campaignToSave = cloneDeep(this.campaign)
        delete campaignToSave.status

        campaignToSave.message.email_configuration_type = this.selectedEmailType

        campaignToSave.segments = campaignToSave.segments.map((obj) => obj._id)
        campaignToSave.products = campaignToSave.products.map((obj) => obj.sku)
        if (this.selectedEmailType === 'SIMPLE') {
          campaignToSave.message.simple_email_configuration.click_to_call_button_link = this.formatedLink
          delete campaignToSave.message.html_email_configuration
          delete campaignToSave.message.builder_email_configuration
        } else if (this.selectedEmailType === 'BUILDER') {
          delete campaignToSave.message.simple_email_configuration
          delete campaignToSave.message.html_email_configuration
        } else if (this.selectedEmailType === 'HTML') {
          delete campaignToSave.message.simple_email_configuration
          delete campaignToSave.message.builder_email_configuration
        }
        deepDelete(campaignToSave, '__typename')
        if (isDraft) {
          await this.$apollo.mutate({
            mutation: MUTATION_UPSERT_DRAFT_EMAIL_CAMPAIGN,
            variables: {
              data: campaignToSave,
            },
            context: {
              uri: this.$microservicesUrls['campaigns'],
            },
          })

          Swal.fire({
            text: 'Rascunho salvo',
            icon: 'success',
            showConfirmButton: false,
            timer: 1000,
            customClass: {
              content: 'content-class',
            },
          })
          this.returnToList()
        } else {
          await this.$apollo.mutate({
            mutation: MUTATION_UPSERT_EMAIL_CAMPAIGN,
            variables: {
              data: campaignToSave,
            },
            context: {
              uri: this.$microservicesUrls['campaigns'],
            },
          })

          Swal.fire({
            text: 'Sua campanha foi agendada',
            icon: 'success',
            showConfirmButton: false,
            timer: 1000,
            customClass: {
              content: 'content-class',
            },
          })
          this.returnToList()
        }
      } catch (error) {
        Swal.fire({
          text: 'Erro ao salvar campanha',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
      }
    },
    removeProduct(index) {
      this.campaign.products.splice(index, 1)
    },
    returnToList() {
      this.$router.push('/campanhas/email').catch(() => {})
    },
    goToLink() {
      window.open(this.formatedLink)
    },
    async modifyHeaderImg({ base64, filename }) {
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.campaign.message.simple_email_configuration.header_image = url
        })
    },
    deleteHeaderImage() {
      this.campaign.message.simple_email_configuration.header_image = ''
    },
    async modifyBodyImg({ base64, filename }) {
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.campaign.message.simple_email_configuration.image = url
        })
    },
    deleteBodyImage() {
      this.campaign.message.simple_email_configuration.image = ''
    },
    async prebuildAutoCampaign() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }

      this.campaign.segments.push(this.quickAction.segmentation.segment)
      this.campaign.title = this.quickAction.segmentation.title
      this.campaign.description = this.quickAction.segmentation.description

      this.unsetQuickAction()
    },
  },
  validations() {
    if (this.tryingToSaveAsDraft) {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            notOnlySpace,
          },
          frequency: {
            interval: {
              minValue: minValue(1),
            },
          },
        },
      }
    } else if (this.tryingSendEmailPreview) {
      return {
        campaign: {
          message: {
            subject: {
              required,
              notOnlySpace,
            },
            processed_html: {
              required,
              notOnlySpace,
            },
            ...(this.selectedEmailType === 'SIMPLE'
              ? {
                  simple_email_configuration: {
                    subject: {
                      required,
                      maxLength: maxLength(this.maxSubjectLength),
                      notOnlySpace,
                    },
                    text: {
                      required,
                      maxLength: maxLength(this.maxTextLength),
                      notOnlySpace,
                    },
                    click_to_call_button_text: {
                      required,
                      maxLength: maxLength(this.maxButtonTextLength),
                      notOnlySpace,
                    },
                    click_to_call_button_link: {
                      required,
                      notOnlySpace,
                      isUrl,
                    },
                    footer: {
                      required,
                      maxLength: maxLength(this.maxFooterLength),
                      notOnlySpace,
                    },
                    image: {
                      required,
                    },
                    header_image: {
                      required,
                    },
                  },
                }
              : {}),
            ...(this.selectedEmailType === 'HTML'
              ? {
                  html_email_configuration: {
                    url: {
                      notOnlySpace,
                      isUrl,
                    },
                  },
                }
              : {}),
          },
        },
      }
    } else {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          segments: {
            required,
            minLength: minLength(1),
          },
          initial_date: {
            required,
          },
          final_date: {
            required,
          },
          message: {
            send_date: {
              required,
              isFuture,
            },
            send_time: {
              required,
              timeIsFuture: function (value) {
                return timeIsFuture(this.campaign.message.send_date, value)
              },
            },
            subject: {
              required,
              notOnlySpace,
            },
            ...(this.selectedEmailType === 'SIMPLE'
              ? {
                  simple_email_configuration: {
                    subject: {
                      required,
                      maxLength: maxLength(this.maxSubjectLength),
                      notOnlySpace,
                    },
                    text: {
                      required,
                      maxLength: maxLength(this.maxTextLength),
                      notOnlySpace,
                    },
                    click_to_call_button_text: {
                      required,
                      maxLength: maxLength(this.maxButtonTextLength),
                      notOnlySpace,
                    },
                    click_to_call_button_link: {
                      required,
                      notOnlySpace,
                      isUrl,
                    },
                    footer: {
                      required,
                      maxLength: maxLength(this.maxFooterLength),
                      notOnlySpace,
                    },
                    image: {
                      required,
                    },
                    header_image: {
                      required,
                    },
                  },
                }
              : {}),
            ...(this.selectedEmailType === 'HTML'
              ? {
                  html_email_configuration: {
                    url: {
                      notOnlySpace,
                      isUrl,
                    },
                  },
                }
              : {}),
          },
          frequency: {
            interval: {
              minValue: minValue(1),
            },
          },
        },
      }
    }
  },
}
</script>
<style lang="scss">
$primary-color: $cinza-l70;

.email-marketing {
  .hint-text {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: $cinza-d10;
  }

  .background-img-header {
    background-color: $primary-color;
    height: 200px;
  }

  .background-img-content {
    background-color: $primary-color;
    height: 350px;
  }

  .img-text {
    height: 85px;
  }

  .email-footer {
    background-color: $cinza-l90;
    text-align: center;

    span {
      font-size: 12px;
    }
  }
}
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot textarea {
  margin-right: 2px;
  margin-top: 5px !important;
}
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot {
  margin-bottom: 5px;
}
</style>
